import React, {PropsWithChildren, useEffect, useRef, useState} from 'react';
import {IElementLineV2} from '../../../../incase-domain/elements/shapes/lineV2';
import useElementActions from '../../../../utils/hooks/slideUpdates/useElementActions';
import {IElementArrowV2} from '../../../../incase-domain/elements/shapes/arrowV2';
import usePresScale from '../../../util/usePresScale';

interface LineElementWrapperProps {
    isActive?: boolean;
    onOptions?: (id: string, silent?: boolean) => void;
    element: IElementLineV2 | IElementArrowV2;
    elementIndex: number;
}

function LineElementWrapper({
    element,
    elementIndex,
    isActive,
    onOptions,
    children,
}: PropsWithChildren<LineElementWrapperProps>): JSX.Element {
    const SNAP_THRESHOLD = 10; // pixels
    const {updateElement} = useElementActions();
    const ref = useRef<HTMLDivElement>(null);
    const {slideRect, transformPointFromFraction, transformPointToFraction} = usePresScale();
    const [draggingPoint, setDraggingPoint] = useState<null | 'start' | 'end' | 'line'>(null);
    const [startPointDiff, setStartPointDiff] = useState<{dx: number; dy: number} | null>(null);
    const [startPoint, setStartPoint] = useState<{x: number; y: number}>(transformPointFromFraction(element.startPoint));
    const [endPoint, setEndPoint] = useState<{x: number; y: number}>(transformPointFromFraction(element.endPoint));

    useEffect(() => {
        setStartPoint(transformPointFromFraction(element.startPoint));
        setEndPoint(transformPointFromFraction(element.endPoint));
    }, [element.endPoint, element.startPoint, transformPointFromFraction]);

    const lineElement = ref.current?.querySelector('line:not(.invisibleClickable)') as SVGLineElement | null;
    const clickableLineWrapper = ref.current?.querySelector('line.invisibleClickable') as SVGLineElement | null;
    const startPointHandle = ref.current?.querySelector('#startPoint') as HTMLDivElement | null;
    const endPointHandle = ref.current?.querySelector('#endPoint') as HTMLDivElement | null;

    useEffect(() => {
        if (!lineElement || !clickableLineWrapper || !startPointHandle || !endPointHandle || !startPoint || !endPoint) return;

        lineElement.setAttribute('x1', String(startPoint.x));
        lineElement.setAttribute('y1', String(startPoint.y));
        lineElement.setAttribute('x2', String(endPoint.x));
        lineElement.setAttribute('y2', String(endPoint.y));

        clickableLineWrapper.setAttribute('x1', String(startPoint.x));
        clickableLineWrapper.setAttribute('y1', String(startPoint.y));
        clickableLineWrapper.setAttribute('x2', String(endPoint.x));
        clickableLineWrapper.setAttribute('y2', String(endPoint.y));

        startPointHandle.style.top = `${startPoint.y}px`;
        startPointHandle.style.left = `${startPoint.x}px`;
        endPointHandle.style.top = `${endPoint.y}px`;
        endPointHandle.style.left = `${endPoint.x}px`;
    }, [lineElement, clickableLineWrapper, startPointHandle, endPointHandle, startPoint, endPoint]);

    useEffect(() => {
        if (!slideRect || !draggingPoint) return;

        const handleMovePoint = (e: MouseEvent): void => {
            let x = (e as MouseEvent).clientX - slideRect.left;
            if (x < 0) x = 0;
            if (x > slideRect.width) x = slideRect.width;
            let y = (e as MouseEvent).clientY - slideRect.top;
            if (y < 0) y = 0;
            if (y > slideRect.height) y = slideRect.height;

            const otherPoint = transformPointFromFraction(draggingPoint === 'start' ? endPoint : startPoint);
            if (otherPoint) {
                const dx = x - otherPoint.x;
                const dy = y - otherPoint.y;
                if (Math.abs(dx) < SNAP_THRESHOLD) x = otherPoint.x;
                if (Math.abs(dy) < SNAP_THRESHOLD) y = otherPoint.y;
            }

            if (draggingPoint === 'start') {
                setStartPoint({x, y});
            } else if (draggingPoint === 'end') {
                setEndPoint({x, y});
            }
        };

        const handleMoveLine = (e: MouseEvent | React.MouseEvent<HTMLDivElement>): void => {
            if (!startPointDiff || !startPoint || !endPoint) return;

            let x1 = e.clientX - slideRect.left - startPointDiff.dx;
            let y1 = e.clientY - slideRect.top - startPointDiff.dy;
            let x2 = x1 + (endPoint.x - startPoint.x);
            let y2 = y1 + (endPoint.y - startPoint.y);

            if (x1 < 0) {
                x1 = 0;
                x2 = x1 + (endPoint.x - startPoint.x);
            }
            if (x1 > slideRect.width) {
                x1 = slideRect.width;
                x2 = x1 + (endPoint.x - startPoint.x);
            }
            if (y1 < 0) {
                y1 = 0;
                y2 = y1 + (endPoint.y - startPoint.y);
            }
            if (y1 > slideRect.height) {
                y1 = slideRect.height;
                y2 = y1 + (endPoint.y - startPoint.y);
            }
            if (x2 < 0) {
                x2 = 0;
                x1 = x2 - (endPoint.x - startPoint.x);
            }
            if (x2 > slideRect.width) {
                x2 = slideRect.width;
                x1 = x2 - (endPoint.x - startPoint.x);
            }
            if (y2 < 0) {
                y2 = 0;
                y1 = y2 - (endPoint.y - startPoint.y);
            }
            if (y2 > slideRect.height) {
                y2 = slideRect.height;
                y1 = y2 - (endPoint.y - startPoint.y);
            }

            setStartPoint({x: x1, y: y1});
            setEndPoint({x: x2, y: y2});
        };

        const onMouseMove = (e: MouseEvent): void => {
            if (draggingPoint === 'line') {
                handleMoveLine(e);
            } else {
                handleMovePoint(e);
            }
        };

        const onMouseUp = (): void => {
            if (!startPoint || !endPoint) return;
            updateElement(elementIndex, {
                ...element,
                startPoint: transformPointToFraction(startPoint),
                endPoint: transformPointToFraction(endPoint),
            });

            setStartPointDiff(null);
            setDraggingPoint(null);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);

        // eslint-disable-next-line consistent-return
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [
        draggingPoint,
        element,
        elementIndex,
        endPoint,
        endPointHandle,
        lineElement,
        clickableLineWrapper,
        slideRect,
        startPoint,
        startPointDiff,
        startPointHandle,
        transformPointFromFraction,
        transformPointToFraction,
        updateElement,
    ]);

    return (
        <div
            ref={ref}
            className={`c-lineElementWrapper ${isActive ? 'is-active' : ''}`}
            onMouseDown={(e) => {
                e.preventDefault();
                onOptions?.(element.id);
                setStartPointDiff({
                    dx: e.clientX - (slideRect?.left || 0) - (startPoint?.x || 0),
                    dy: e.clientY - (slideRect?.top || 0) - (startPoint?.y || 0),
                });
                setDraggingPoint('line');
            }}
        >
            {isActive && (
                <div className="c-lineElementWrapper__handlers">
                    <div
                        id="startPoint"
                        className="c-lineElementWrapper__handlers_circle"
                        style={{
                            top: startPoint?.y || 0,
                            left: startPoint?.x || 0,
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setDraggingPoint('start');
                        }}
                    />
                    <div
                        id="endPoint"
                        className="c-lineElementWrapper__handlers_circle"
                        style={{
                            top: endPoint?.y || 0,
                            left: endPoint?.x || 0,
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setDraggingPoint('end');
                        }}
                    />
                </div>
            )}
            {children}
        </div>
    );
}

export default LineElementWrapper;
